import Filter from '../Filter/Filter';
import TooltipWidget from '../Tooltip/Tooltip';

import Box from '@mui/material/Box';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import {visuallyHidden} from '@mui/utils';
import PropTypes from 'prop-types';
import React from 'react';
import {useTranslation} from 'react-i18next';
import './DataTable.css';

export const descendingComparator = (a, b, orderBy) => {
  const aValue =
    typeof a[orderBy]?.value === 'undefined' ? a[orderBy] : a[orderBy].value;
  const bValue =
    typeof b[orderBy]?.value === 'undefined' ? b[orderBy] : b[orderBy].value;

  if (bValue < aValue) {
    return -1;
  }
  if (bValue > aValue) {
    return 1;
  }
  return 0;
};

export const getComparator = (order, orderBy) => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

export const stableSort = (array, comparator) => {
  if (!array || !Array.isArray(array)) {
    return [];
  }
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

export const EnhancedTableHead = (props) => {
  const {order, orderBy, onRequestSort, headCells} = props;
  const [filter, setFilter] = React.useState('');
  const {t} = useTranslation();

  const createSortHandler = (property) => {
    onRequestSort(property);
  };
  const createFilterHandler = (val, propertyName) => {
    setFilter(val);
    props.onRequestFilter(val, propertyName);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells?.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.alignment}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            width={headCell.width}
          >
            {headCell.sortingEnabled ? (
              <TableSortLabel
                data-testid={headCell.id + '_SORT'}
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={() => createSortHandler(headCell.id)}
              >
                {t(headCell.label)}
                {headCell.infoIconText && (
                  <TooltipWidget
                    tooltip={headCell.infoIconText}
                    placement="top"
                  />
                )}

                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : headCell.filterEnabled ? (
              <span className="tableFilterLabel">
                <span>
                  {t(headCell.label)}
                  {headCell.infoIconText && (
                    <TooltipWidget
                      tooltip={headCell.infoIconText}
                      placement="top"
                    />
                  )}
                </span>
                <Filter
                  propertyName={headCell.id}
                  selectedFilter={filter}
                  filterOptionList={headCell.filterList}
                  createFilterHandler={createFilterHandler}
                />
              </span>
            ) : (
              <>
                {t(headCell.label)}
                {headCell.infoIconText && (
                  <TooltipWidget
                    tooltip={headCell.infoIconText}
                    placement="top"
                  />
                )}
              </>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

export const EnhancedTableRows = (props) => {
  return (
    <TableBody>
      {props.visibleRows?.length ? (
        props.visibleRows.map((row, index) => {
          const labelId = `data-table-${index}`;
          return (
            <TableRow
              hover
              tabIndex={-1}
              key={labelId}
              sx={{cursor: 'pointer'}}
            >
              {Object.keys(row).map((key) => {
                return (
                  <TableCell
                    key={'table-cell-' + key}
                    align={props.rowAlignment?.[key] || 'left'}
                    component="th"
                    scope="row"
                    padding="normal"
                  >
                    {row[key]?.component}
                  </TableCell>
                );
              })}
            </TableRow>
          );
        })
      ) : (
        <TableRow
          hover
          tabIndex={-1}
          key={'defaultRow'}
          sx={{cursor: 'pointer'}}
        >
          <TableCell colSpan={props.defaultColSpan}>
            {props.noRecordFoundComponent}
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  );
};

EnhancedTableRows.propTypes = {
  rowAlignment: PropTypes.object,
};
